// src/scenes/MenuScene.ts
//
// Phase 10 — Title screen with New Run / Continue (conditional) / How to Play.
// First-launch detection auto-opens the HowToPlayModal once.

import Phaser from 'phaser';
import { GameState } from '../state/GameState';
import { loadRun, clearSave } from '../systems/save';
import { HowToPlayModal, isFirstLaunch, markFirstLaunchSeen } from '../ui/HowToPlayModal';
import { addCrispText } from '../ui/textHelper';

export class MenuScene extends Phaser.Scene {
  private modal!: HowToPlayModal;

  constructor() {
    super({ key: 'MenuScene' });
  }

  create(): void {
    this.modal = new HowToPlayModal(this);

    const W = this.scale.width;
    const H = this.scale.height;

    // Full-screen background image; fall back to the old navy+tile if not loaded
    if (this.textures.exists('bg-homescreen')) {
      this.add
        .image(W / 2, H / 2, 'bg-homescreen')
        .setDisplaySize(W, H)
        .setOrigin(0.5);
    } else {
      this.add.rectangle(0, 0, W, H, 0x0d1b2a).setOrigin(0, 0);
      if (this.textures.exists('bg-tile')) {
        const tileW = 128;
        const cols = Math.ceil(W / tileW) + 1;
        const rows = Math.ceil(H / tileW) + 1;
        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
            this.add
              .image(c * tileW, r * tileW, 'bg-tile')
              .setOrigin(0, 0)
              .setAlpha(0.08);
          }
        }
      }
      addCrispText(this, W / 2, 80, 'ROGUELETTE', {
        color: '#d4af37',
        fontSize: '32px',
      }).setOrigin(0.5);
      addCrispText(this, W / 2, 116, 'a roulette roguelite', {
        color: '#aaaaaa',
        fontSize: '12px',
      }).setOrigin(0.5);
      if (this.textures.exists('portrait-dealer')) {
        this.add
          .image(W / 2, 240, 'portrait-dealer')
          .setDisplaySize(120, 160)
          .setOrigin(0.5);
      }
    }

    // Buttons — positioned over the lower portion of the artwork
    const btnY0 = 450;
    const btnGap = 54;
    this.makeButton(W / 2, btnY0, 'New Run', () => this.startNewRun());
    if (loadRun() !== null) {
      this.makeButton(W / 2, btnY0 + btnGap, 'Continue', () => this.continueRun());
      this.makeButton(W / 2, btnY0 + btnGap * 2, 'How to Play', () => this.modal.open());
    } else {
      this.makeButton(W / 2, btnY0 + btnGap, 'How to Play', () => this.modal.open());
    }

    // First-launch trigger
    if (isFirstLaunch()) {
      markFirstLaunchSeen();
      this.modal.open();
    }
  }

  private makeButton(x: number, y: number, label: string, onClick: () => void): void {
    const rect = this.add
      .rectangle(x, y, 220, 40, 0x064020)
      .setInteractive()
      .setStrokeStyle(1, 0xd4af37);
    addCrispText(this, x, y, label, {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      color: '#ffffff',
      fontSize: '15px',
    }).setOrigin(0.5);
    rect.on('pointerdown', onClick);
  }

  private startNewRun(): void {
    clearSave();
    const gs = new GameState();
    this.scene.start('RunStart', { gameState: gs });
  }

  private continueRun(): void {
    const snap = loadRun();
    if (!snap) {
      this.startNewRun();
      return;
    }
    const gs = GameState.fromJSON(snap);
    this.scene.start('Round', { gameState: gs });
  }
}
